import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import './RecallList.css';

const RecallList = () => {
  const navigate = useNavigate();
  const { fetchWithAuth, isAdmin } = useAuth();
  const [records, setRecords] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLocations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetchWithAuth('/api/consignment-locations');
      if (res.ok) {
        const list = await res.json();
        setLocations(Array.isArray(list) ? list : []);
      }
    } catch {
      setLocations([]);
    }
  }, [fetchWithAuth, isAdmin]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      let url = '/api/recall/records';
      if (isAdmin && selectedLocationId !== '') {
        url += `?consignment_location_id=${encodeURIComponent(selectedLocationId)}`;
      }
      const res = await fetchWithAuth(url);
      if (!res.ok) throw new Error('Failed to load recall list');
      const data = await res.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, isAdmin, selectedLocationId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <Layout>
      <div className="recall-list-container">
        <div className="page-header">
          <button type="button" className="back-button" onClick={() => navigate('/')}>
            ← Back
          </button>
          <div className="header">
            <h1>Recall List</h1>
            <p className="header-desc">All uploaded recall items. Operators see pending items on the mobile Recall screen by location.</p>
          </div>
        </div>
        <div className="content">
          {isAdmin && locations.length > 0 && (
            <div className="filter-row">
              <label>
                <span className="filter-label">Location:</span>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name || loc.code || `Location ${loc.id}`}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {error && <div className="error-message">{error}</div>}
          {loading ? (
            <p>Loading…</p>
          ) : records.length === 0 ? (
            <p className="empty-message">No recall records. Upload a recall Excel from Recall Import.</p>
          ) : (
            <div className="recall-table-wrap">
              <table className="recall-table">
                <thead>
                  <tr>
                    <th>Transaction</th>
                    <th>Location</th>
                    <th>Part Number</th>
                    <th>Part Description</th>
                    <th>Qty</th>
                    <th>Status</th>
                    <th>Fulfilled</th>
                    <th>Dispatched at</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id}>
                      <td>{r.transactionId || '—'}</td>
                      <td>{r.consignmentLocationName || (r.consignmentLocationId != null ? `#${r.consignmentLocationId}` : '—')}</td>
                      <td>{r.partNumber || '—'}</td>
                      <td className="desc-cell">{r.partDescription || '—'}</td>
                      <td>{r.quantity ?? '—'}</td>
                      <td><span className={`status-badge status-${r.status || 'pending'}`}>{r.status || 'pending'}</span></td>
                      <td>{r.fulfilledQty != null ? r.fulfilledQty : '—'}</td>
                      <td>{formatDate(r.dispatchedAt)}</td>
                      <td>{formatDate(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RecallList;
